import { type BaseSpec, type ViewColumnDef } from "../../api-types";
import type { SqliteColumnDef, SqliteTableDef } from "../schema";
import { type DatabaseTables, type TableName } from "../types";

function sanitizeAsColumnName(s: string) {
	return s.toLowerCase().trim().replace(/\s+/g, "_");
}

export function assertViewExists(baseSpec: BaseSpec, viewId: string) {
	const viewSpec = baseSpec.views[viewId];
	if (!viewSpec) {
		throw new Error(`View ${viewId} not found`);
	}
	return viewSpec;
}

export function assertTableExists<Tables extends DatabaseTables>(
	baseSpec: BaseSpec,
	table: TableName<Tables>
) {
	const tableSpec = baseSpec.tables[table];
	if (!tableSpec) {
		throw new Error(`Table ${table} not found`);
	}
	return tableSpec;
}

export const reactionTable = (reactionTableName: string): SqliteTableDef => ({
	tableName: reactionTableName,
	columns: [
		{ name: "id", type: "TEXT", primaryKey: true, nullable: false },
		{ name: "linkedRecordId", type: "TEXT", nullable: false },
		{ name: "user", type: "TEXT", nullable: false },
		{ name: "createdAt", type: "INTEGER", nullable: false },
	],
});

//
// Append column functions.
//

export function newUniqueColumn<Tables extends DatabaseTables>(
	table: TableName<Tables>,
	columns: SqliteColumnDef[]
): {
	sqliteColumnDef: SqliteColumnDef;
	viewColumnDef: ViewColumnDef;
} {
	const { name, header } = (function () {
		//
		// Find a unique name and header for this column. Both the header and the name have to be
		// not taken already. For example, if the name `column_1` is not taken, but `Column 1` is,
		// we consider this a collision and continue the search.
		//

		const existingColumnNames = new Set(columns.map((column) => column.name));
		const existingHeaders = new Set(columns.map((column) => column.header));

		let counter = 1;
		let newHeader = `Column ${counter}`;
		let newColumnName = sanitizeAsColumnName(newHeader);
		while (existingHeaders.has(newHeader) || existingColumnNames.has(newColumnName)) {
			newHeader = `Column ${counter}`;
			newColumnName = sanitizeAsColumnName(newHeader);
			counter++;
		}

		return { name: newColumnName, header: newHeader };
	})();

	return {
		sqliteColumnDef: { name, type: "TEXT", header },
		viewColumnDef: { linkedTable: table, linkedColumn: name },
	};
}

export function mutateAppendColumnToTable<Tables extends DatabaseTables>(
	baseSpec: BaseSpec,
	table: TableName<Tables>,
	column: SqliteColumnDef
) {
	const tableSpec = assertTableExists(baseSpec, table);
	const newColumns = [...tableSpec.columns, column];
	baseSpec.tables[table] = { ...tableSpec, columns: newColumns };
	return baseSpec;
}

export function mutateAppendColumnToAllViews<Tables extends DatabaseTables>(
	baseSpec: BaseSpec,
	table: TableName<Tables>,
	column: ViewColumnDef
) {
	for (const [viewId, viewSpec] of Object.entries(baseSpec.views)) {
		if (table !== viewSpec.query.spec[0].table) {
			continue;
		}

		const newColumns = [...viewSpec.columns, column];
		baseSpec.views[viewId] = { ...viewSpec, columns: newColumns };
	}
	return baseSpec;
}

//
// Delete column functions.
//

export function mutateDeleteColumnFromTable<Tables extends DatabaseTables>(
	baseSpec: BaseSpec,
	table: TableName<Tables>,
	columnName: string
) {
	const tableSpec = assertTableExists(baseSpec, table);
	const newColumns = tableSpec.columns.filter((c) => c.name !== columnName);
	baseSpec.tables[table] = { ...tableSpec, columns: newColumns };
	return baseSpec;
}

export function mutateDeleteColumnFromAllViews<Tables extends DatabaseTables>(
	baseSpec: BaseSpec,
	table: TableName<Tables>,
	columnName: string
) {
	for (const [viewId, viewSpec] of Object.entries(baseSpec.views)) {
		if (table !== viewSpec.query.spec[0].table) {
			continue;
		}

		const newColumns = viewSpec.columns.filter((c) => c.linkedColumn !== columnName);
		baseSpec.views[viewId] = { ...viewSpec, columns: newColumns };
	}
}

//
// Rename column functions.
//

export function mutateRenameColumnFromTable<Tables extends DatabaseTables>(
	baseSpec: BaseSpec,
	table: TableName<Tables>,
	oldColumnName: string,
	newHeader: string,
	newColumnName: string
) {
	const tableSpec = assertTableExists(baseSpec, table);
	const index = tableSpec.columns.findIndex((c) => c.name === oldColumnName);
	const column = tableSpec.columns[index];
	if (index === -1 || !column) {
		throw new Error(`Column not found`);
	}

	const columns: SqliteColumnDef[] = structuredClone(tableSpec.columns);
	columns[index] = { ...column, header: newHeader, name: newColumnName };
	baseSpec.tables[table] = { ...tableSpec, columns };
	return baseSpec;
}

export function mutateRenameColumnFromAllViews<Tables extends DatabaseTables>(
	baseSpec: BaseSpec,
	table: TableName<Tables>,
	oldColumnName: string,
	newColumnName: string
) {
	for (const [viewId, viewSpec] of Object.entries(baseSpec.views)) {
		if (table !== viewSpec.query.spec[0].table) {
			continue;
		}

		const index = viewSpec.columns.findIndex((c) => c.linkedColumn === oldColumnName);
		const column = viewSpec.columns[index];
		if (index === -1 || !column) {
			throw new Error(`Column not found`);
		}

		const columns: ViewColumnDef[] = structuredClone(viewSpec.columns);
		columns[index] = { ...column, linkedColumn: newColumnName };
		baseSpec.views[viewId] = { ...viewSpec, columns };
	}
	return baseSpec;
}

//
// Update column functions.
//

export function mutateUpdateColumnFromTable<Tables extends DatabaseTables>(
	baseSpec: BaseSpec,
	table: TableName<Tables>,
	column: SqliteColumnDef
) {
	const tableSpec = assertTableExists(baseSpec, table);
	const index = tableSpec.columns.findIndex((c: any) => c.name === column.name);
	const oldColumn = tableSpec.columns[index];
	if (index === -1 || !oldColumn) {
		throw new Error(`Column not found`);
	}

	const columns: SqliteColumnDef[] = structuredClone(tableSpec.columns);
	columns[index] = { ...oldColumn, ...column };
	baseSpec.tables[table] = { ...tableSpec, columns };
	return baseSpec;
}

export function mutateCreateReactionTable(baseSpec: BaseSpec, reactionTableName: string) {
	// Create a new reaction table with the standard reaction schema
	baseSpec.tables[reactionTableName] = {
		...reactionTable(reactionTableName),
		permissions: [],
	};
	return baseSpec;
}

export function mutateRemoveReactionTable(baseSpec: BaseSpec, reactionTableName: string) {
	// Remove the reaction table from the base spec
	delete baseSpec.tables[reactionTableName];
	return baseSpec;
}
